/**
 * Decides HOW a map is drawn, so a device that cannot draw the fancy kind
 * still gets a usable map instead of a gray, un-pannable rectangle.
 *
 * Vector maps (needed for AdvancedMarker) require WebGL and a real cloud Map
 * ID. Without WebGL the vector renderer paints gray; with no Map ID the only
 * way to "work" used to be the 'DEMO_MAP_ID' placeholder, which Google
 * restricts. In both cases RASTER rendering + the classic Marker works.
 */

export interface MapRendering {
  /** True when the vector renderer (Map ID + AdvancedMarker) is safe to use. */
  vector: boolean;
  /** Real cloud Map ID; undefined in raster mode. Never a placeholder. */
  mapId: string | undefined;
}

let webglProbe: boolean | undefined;

/**
 * Test seam: forget the memoized WebGL probe.
 * @internal
 */
export function resetWebGLProbeForTests(): void {
  webglProbe = undefined;
}

/**
 * True when this browser can create a WebGL context. Never throws. Probed once
 * per page load (the answer cannot change mid-session) and the probe context is
 * released immediately so it does not count against the browser's context limit.
 */
export function isWebGLAvailable(): boolean {
  if (webglProbe !== undefined) return webglProbe;
  try {
    if (typeof document === 'undefined') return false;
    const canvas = document.createElement('canvas');
    const gl = (canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    webglProbe = Boolean(gl);
    gl?.getExtension?.('WEBGL_lose_context')?.loseContext();
  } catch {
    webglProbe = false;
  }
  return webglProbe;
}

/** Shown in place of a map when Google rejected our key/referrer. */
export const MAPS_AUTH_FAILED_MESSAGE = "Map couldn't load. Open in Google Maps";

/**
 * Same, for surfaces with no "Open in Google Maps" button to point at (survey
 * address, survey site map, team time map).
 */
export const MAPS_LOAD_FAILED_MESSAGE = "Map couldn't load.";

/**
 * The <Map> props that differ between the two modes: a real Map ID for vector,
 * RASTER (and no Map ID at all) otherwise.
 */
export function mapRenderingProps(
  rendering: MapRendering,
): { mapId: string | undefined } | { renderingType: 'RASTER' } {
  return rendering.vector ? { mapId: rendering.mapId } : { renderingType: 'RASTER' as const };
}

/** Vector only when WebGL works AND a real Map ID is configured. */
export function resolveMapRendering(): MapRendering {
  const mapId = (process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || '').trim();
  if (mapId && isWebGLAvailable()) return { vector: true, mapId };
  return { vector: false, mapId: undefined };
}
